import { SetStateAction, useMemo, useState } from 'react';
import { useDebounce } from 'common/useDebounce';
import { useTranslation } from 'react-i18next';
import { useAtom, useSetAtom, WritableAtom } from 'jotai';
import { useMnosAutocomplete } from './hooks/useMnosAutocomplete';
import { Autocomplete } from 'src/common/components/Inputs/Autocomplete/Autocomplete';

type MNOSearchFilterProps = {
  mnoFilterAtom: WritableAtom<(string | null)[], [SetStateAction<(string | null)[]>], void>;
  mnoNamesFilterAtom: WritableAtom<string[], [SetStateAction<string[]>], void>;
  mnoIdFilterAtom: WritableAtom<(number | null)[], [SetStateAction<(number | null)[]>], void>;
  mnoHintsAtom: WritableAtom<any, [any], void>;
};

const MAX_MNOS_TO_FILTER = 10;

export const MNOSearchFilter = ({
  mnoHintsAtom,
  mnoNamesFilterAtom,
  mnoIdFilterAtom,
  mnoFilterAtom,
}: MNOSearchFilterProps) => {
  const { t } = useTranslation();

  const [hints, removeHints] = useAtom(mnoHintsAtom);

  const [mnoSearch, setMnoSearch] = useState('');
  const debounceMnoSearch = useDebounce(mnoSearch, 500);
  const blankValuesEnabled = !debounceMnoSearch.length;

  const [mnos, setMnos] = useAtom(mnoFilterAtom);
  const setMnoNames = useSetAtom(mnoNamesFilterAtom);
  const setMnoIds = useSetAtom(mnoIdFilterAtom);

  const {
    options: mnosOptions,
    isSelectedOptionFetching,
    isFetched,
    selectedOptions,
    hintOptions,
    updateSelectedOptions,
    hasNextPage,
    fetchNextPage,
  } = useMnosAutocomplete({
    search: debounceMnoSearch,
    initialIds: mnos,
    hints: hints,
    onSelectedChange: (options) => {
      setMnos(options.map(({ id }) => id));
      setMnoNames(options.map(({ name }) => name));
      setMnoIds(options.map(({ mnoId }) => mnoId));
    },
  });

  const hasMaxMnoLimitsOccurs = mnos && mnos.length >= MAX_MNOS_TO_FILTER;
  const errors = useMemo(() => {
    let errors: string[] = [];
    if (hasMaxMnoLimitsOccurs) {
      errors = [t('common.limitReached')];
    }

    return errors;
  }, [hasMaxMnoLimitsOccurs]);

  return (
    <Autocomplete
      data-testid="mno-multiselect"
      placeholder={t('simInventory.mnoFilterPlaceholder')}
      skeletonRepeats={mnos.length}
      inputLoading={isSelectedOptionFetching}
      options={mnosOptions}
      value={selectedOptions}
      hints={hintOptions}
      inputValue={mnoSearch}
      //@ts-expect-error Temporary until it will unifyit in sim inventory
      onChange={updateSelectedOptions}
      onSearch={setMnoSearch}
      loading={!isFetched}
      onHintRemove={removeHints}
      errors={errors}
      disallowSelecting={hasMaxMnoLimitsOccurs}
      disallowSelectingReason={t('common.limitReached')}
      blankValueEnabled={blankValuesEnabled}
      hasMore={hasNextPage}
      onMoreShouldBeLoad={fetchNextPage}
    />
  );
};
